import BigNumber from 'bignumber.js';
import { useInfiniteQuery, useQuery, useQueryState, useNavigate } from '@ywwwtseng/react-kit';
import {
  Typography,
  openTelegramLink,
} from '@ywwwtseng/tma-react-kit';
import * as web3 from '@ywwwtseng/web3';
import * as schema from '@/db/schema';
import { Users } from 'lucide-react';
import { RedPackage2 } from '@/components/icons';
import { EventMarquee } from '@/components/common/EventMarquee';
import { Chat } from '@/components/common/Chat';
import { List } from '@/components/common/List';
import { CountdownTimer } from '@/components/common/CountdownTimer';
import { Mission } from '@/components/common/Mission';
import { WatchAdReward } from '@/components/common/WatchAdReward';
import { ShareRedPacketPromptModal } from '@/components/common/ShareRedPacketPromptModal';
import * as utils from '@/utils';
import type { Gift, MissionWithInfo } from '@/types';

export default function EarnScreen() {

  const { data: gifts, fetchNextPage } = useInfiniteQuery<Gift[]>('public_gifts', {
    params: {
      status: 'active',
      limit: 20,
    },
    refetchOnMount: true,
  });

  const { data: overview } = useQuery<{
    recent_gift_chats: (typeof schema.tg_chats.$inferSelect)[];
    top_gift_chats: (typeof schema.tg_chats.$inferSelect)[];
  }>('overview', {
    refetchOnMount: true,
  });

  // 获取所有启用的任务（包含信息和兑换状态）
  const { data: missions, refetch: refetchMissions } = useQuery<MissionWithInfo[]>('missions', {
    refetchOnMount: true,
  });

  const { recent_gift_chats, top_gift_chats } = overview ?? {
    recent_gift_chats: [],
    top_gift_chats: [],
  };

  return (
    <>
      <div className="flex flex-col items-center justify-start w-full h-full gap-5 py-3 px-5 overflow-x-hidden">

        <EventMarquee />
        {/* 看廣告領獎專區 */}
        <WatchAdReward />
        {/* 任务列表 */}
        {missions && missions.length > 0 && (
          <div className="flex flex-col w-full items-start gap-2">
            <Typography size="2" weight={600} i18n="earn.missions" />
            <div className="flex flex-col w-full gap-1.5">
              {missions?.map((mission) => (
                <Mission key={mission.id} mission={mission} onRedeemSuccess={refetchMissions} />
              ))}
            </div>
          </div>
        )}


        {recent_gift_chats && (
          <div className="flex flex-col w-full items-start gap-2">
            <Typography size="2" weight={600} i18n="earn.latest_gift_groups" />
            <div className="flex w-full gap-2 py-2">
              {recent_gift_chats.map((chat) => (
                <Chat key={chat.id} chat={chat} direction="vertical" className="flex-1 min-w-0" />
              ))}
            </div>
          </div>
        )}

        {top_gift_chats && (
          <div className="flex flex-col w-full items-start gap-2">
            <Typography size="2" weight={600} i18n="earn.top_gift_groups" />
            <div className="flex w-full gap-2 py-2">
              {top_gift_chats.map((chat) => (
                <Chat key={chat.id} chat={chat} direction="vertical" className="flex-1 min-w-0" />
              ))}
            </div>
          </div>
        )}

        {gifts && gifts.length > 0 && (
          <div className="flex flex-col w-full items-start gap-2">
            <Typography size="2" weight={600} i18n="earn.latest_gifts" />

            <List
              showEmptyView={false}
              noDataLabel="common.gifts_empty"
              data={gifts}
              className="flex flex-col w-full gap-2 overflow-y-auto"
              onScrollEnd={() => {
                fetchNextPage();
              }}
            >
              {(gift) => (
                <div
                  key={gift.id}
                  className="card flex items-start w-full gap-2"
                  onClick={() => {
                    if (gift.tg_chat_username) {
                      const message_id = gift.message_id?.split(':')[1];

                      if (message_id) {
                        openTelegramLink(`https://t.me/${gift.tg_chat_username}/${message_id}`);
                      } else {
                        openTelegramLink(`https://t.me/${gift.tg_chat_username}`);
                      }
                    }
                  }}
                >
                  <RedPackage2 width={20} height={20} className="min-w-5 max-w-5" />
                  <div className="flex flex-col w-[calc(100%-28px)] items-start gap-1">
                    <div className="w-full flex items-center gap-2">
                      <Typography size="1" weight={600} ellipsis lineClamp={1}>
                        {gift.tg_chat_name}
                      </Typography>
                      <Typography size="1" weight={600} className="ml-auto">
                        {web3.utils.formatUnits(
                          gift.total,
                          Number(gift.token_id.split(':')[1])
                        )}
                      </Typography>
                      <Typography size="1" weight={600}>
                        {utils.token.getTokenSymbol(gift.token_id)}
                      </Typography>
                    </div>
                    <div className="w-full flex flex-1 items-center gap-1">
                      <Users size={14} />
                      <Typography size="1" weight={600}>
                        {gift.recipients}
                      </Typography>
                      <Typography
                        size="1"
                        weight={600}
                        i18n="common.received"
                        noWrap
                      />
                      <Typography size="1" weight={600} ellipsis lineClamp={1}>
                        {new BigNumber(
                          web3.utils.formatUnits(
                            new BigNumber(gift.total).minus(gift.balance).toFixed(),
                            Number(gift.token_id.split(':')[1])
                          )
                        )
                          .toFixed(6)
                          .replace(/\.?0+$/, '')}
                      </Typography>
                      <Typography size="1" weight={600}>
                        {utils.token.getTokenSymbol(gift.token_id)}
                      </Typography>
                      {gift.status === 'active' && gift.end_at && (
                        <div className="ml-auto">
                          <CountdownTimer gift={gift} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </List>
          </div>
        )}
      </div>
      <ShareRedPacketPromptModal />
    </>
  );
}
